/**
 * MarqueeStrip — pure-CSS infinite right-to-left scroller.
 *
 * Renders the supplied image URLs twice in a row so the loop never breaks
 * (when the first set finishes scrolling past, the duplicate is already in place).
 * The animation duration is computed from the number of items so the speed
 * stays consistent regardless of how many images are uploaded.
 *
 * Props:
 *   images        — array of image URL strings
 *   height        — px height of each tile (default 130)
 *   gap           — px gap between tiles (default 16)
 *   pxPerSecond   — scroll speed (default 60)
 *   testid
 */
import { useMemo } from "react";
import ResponsiveImage from "./ResponsiveImage";

export default function MarqueeStrip({
    images = [],
    height = 130,
    gap = 16,
    pxPerSecond = 60,
    testid = "marquee-strip",
}) {
    const resolved = useMemo(
        () => images.filter(Boolean).map((u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`)),
        [images]
    );
    if (resolved.length === 0) return null;

    // Approx tile width (16:9 aspect ratio) — used to size the animation duration.
    const approxTileWidth = Math.round(height * (16 / 9));
    const totalWidth = (approxTileWidth + gap) * resolved.length;
    const durationSec = Math.max(8, Math.round(totalWidth / pxPerSecond));

    return (
        <div
            data-testid={testid}
            className="relative w-full overflow-hidden bg-slate-950"
            style={{ height }}
        >
            <div
                className="flex items-stretch h-full marquee-track"
                style={{ animationDuration: `${durationSec}s`, gap: `${gap}px` }}
            >
                {[...resolved, ...resolved].map((src, i) => {
                    // We pass the raw `images[i % images.length]` (un-absolutised) to
                    // ResponsiveImage so it can derive the WebP variants. `resolved`
                    // is only used here for the absolute-URL key/index map.
                    const rawSrc = images[i % images.length];
                    return (
                        <div
                            key={`${src}-${i}`}
                            data-testid={`${testid}-tile-${i}`}
                            className="flex-shrink-0 h-full overflow-hidden rounded-md bg-slate-900"
                            style={{ aspectRatio: "16 / 9" }}
                        >
                            <ResponsiveImage
                                src={rawSrc}
                                alt=""
                                className="w-full h-full object-cover object-center select-none"
                            />
                        </div>
                    );
                })}
            </div>

            <style>{`
                .marquee-track {
                    width: max-content;
                    animation-name: marquee-scroll;
                    animation-timing-function: linear;
                    animation-iteration-count: infinite;
                    animation-direction: normal;
                }
                @keyframes marquee-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .marquee-track { animation: none; }
                }
            `}</style>
        </div>
    );
}
